/**
 * Right-pinned ("sticky") table columns.
 *
 * A wide table scrolls horizontally, and the Actions column — always last —
 * is the first thing pushed off-screen. Acting on a row then costs a scroll
 * every time. A column declared `sticky: 'right'` stays parked against the
 * right edge while the rest of the row slides underneath it.
 *
 * The classes live here rather than inline so the header `<th>` and the body
 * `<td>` cannot drift apart: they must agree on the offset and the stacking
 * order or the column splits in two as soon as anyone scrolls.
 *
 * Pure by necessity as well as by taste — `vitest.config.ts` runs in the `node`
 * environment and matches only `.test.ts`, so a `.tsx` test would silently
 * never run. The decisions worth covering therefore live in plain functions.
 */

/** The bit of a scroll container's geometry that decides whether the seam shows. */
export interface HorizontalScrollMetrics {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

/**
 * Whether a right-pinned column currently has content hidden underneath it,
 * and so should draw its leading edge.
 *
 * Drawn only while something is actually under there. A table that fits, or one
 * scrolled all the way right, has nothing behind the pinned column — a shadow
 * then is a seam across a row that is visually continuous, which reads as a
 * rendering fault rather than as an affordance.
 *
 * The 1px slack absorbs sub-pixel layout: a fractional `scrollWidth` can sit a
 * hair above `clientWidth` on a table that fits exactly, and without it such a
 * table would wear a permanent shadow.
 */
export function showsStickyEdge(m: HorizontalScrollMetrics): boolean {
  const { scrollLeft, scrollWidth, clientWidth } = m
  if (!Number.isFinite(scrollLeft) || !Number.isFinite(scrollWidth) || !Number.isFinite(clientWidth)) {
    return false
  }
  return scrollWidth - clientWidth - scrollLeft > 1
}

/** Enough of an element to say whether it scrolls horizontally. */
export interface ScrollableCandidate {
  overflowX: string
  scrollWidth: number
  clientWidth: number
}

/**
 * Whether this element is the one that scrolls a wide table sideways.
 *
 * Both conditions are needed. `overflow-x: auto` on an element whose content
 * fits scrolls nothing, and an element wider than its box does not scroll at
 * all unless its overflow says so — picking either signal alone finds the
 * wrong ancestor. This matters here because the tables nest two candidates:
 * the page's own wrapper and the one `components/ui/table.tsx` renders inside
 * it, and only the inner one actually moves.
 */
export function scrollsHorizontally(el: ScrollableCandidate): boolean {
  const scrolls = el.overflowX === 'auto' || el.overflowX === 'scroll' || el.overflowX === 'overlay'
  return scrolls && el.scrollWidth - el.clientWidth > 1
}

/**
 * Classes for a right-pinned body cell.
 *
 * An opaque background is not optional — without one the scrolling columns show
 * straight through the pinned cell. It is `bg-inherit` rather than a named
 * colour so the cell takes whatever its OWN row is painted: the row already
 * knows about hover, selection, zebra striping and the blue lot-family rows,
 * and restating any of those here would be a second source of truth that
 * disagrees the moment a table styles its rows differently. It would also
 * invent states where none exist — the nested lot rows have no hover, and a
 * pinned cell that lit up on hover would be the only part of the row that did.
 *
 * **The row must therefore carry an opaque background in EVERY state it can be
 * in — resting, hover and selected.** `bg-inherit` is faithful, which cuts both
 * ways: a `<tr>` with no background computes to `transparent`, and a row tinted
 * with a fractional-opacity utility computes to a translucent colour. Either one
 * is inherited exactly as-is and the columns scroll visibly through the pinned
 * cell.
 *
 * The trap in practice is hover. `components/ui/table.tsx` gives every
 * `TableRow` `hover:bg-muted/50` — half-opacity — so a table that looks solid at
 * rest goes see-through under the pointer, which is precisely when someone is
 * reaching for the actions. A table with a pinned column must therefore override
 * that with the opaque `hover:bg-muted`; see `MaterialsTable`.
 */
export const STICKY_CELL_CLASS = 'sticky right-0 z-10 bg-inherit'

/**
 * What a `<TableRow>` hosting a pinned cell must wear, so the cell has an opaque
 * colour to inherit in every state.
 *
 * Named rather than left to each table to remember: the failure is invisible
 * until you hover a row on a table wide enough to scroll, and it shows up as
 * "the actions column is see-through" long after the column was added. Applying
 * this by name is how a new sticky column gets it right by default.
 *
 * `hover:bg-muted` overrides `TableRow`'s own `hover:bg-muted/50` — `cn()` puts
 * the caller's className last, so tailwind-merge lets it win.
 */
export const STICKY_ROW_CLASS = 'bg-background hover:bg-muted'

/**
 * The header cell. `bg-inherit` is no use here: `thead` carries
 * `bg-table-header` but `inherit` resolves against the `<tr>`, which is
 * transparent — so the header's colour is named outright. A table whose header
 * row sets its own colour (the lot table's `[&>th]:bg-blue-50`) overrides this
 * on specificity, which is why that nested header keeps its blue.
 *
 * `z-20` keeps it above the body's pinned cells wherever a header is itself
 * sticky vertically.
 */
export const STICKY_HEAD_CLASS = 'sticky right-0 z-20 bg-table-header'

/** The leading edge, applied only when `showsStickyEdge` says so. */
export const STICKY_EDGE_CLASS = 'shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.18)]'

/** The full class string for a pinned cell, edge included when it applies. */
export function stickyCellClass(kind: 'head' | 'body', showEdge: boolean): string {
  const base = kind === 'head' ? STICKY_HEAD_CLASS : STICKY_CELL_CLASS
  return showEdge ? `${base} ${STICKY_EDGE_CLASS}` : base
}
